import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Settings, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  getInitials,
} from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/api/endpoints/auth';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super admin',
  org_admin: 'Org admin',
  staff: 'Staff',
  customer: 'Customer',
};

/** Avatar + dropdown showing the current user, profile shortcut, and sign out. */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);

  const initials = getInitials(user?.display_name, user?.email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.profile')}
          className="rounded-full"
        >
          <Avatar className="h-8 w-8">
            {user?.avatar_url ? (
              <AvatarImage src={user.avatar_url} alt={user.display_name ?? user.email} />
            ) : null}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-foreground">
              {user?.display_name ?? user?.email ?? '—'}
            </span>
            {user?.email && (
              <span className="truncate text-xs font-normal text-muted-foreground">
                {user.email}
              </span>
            )}
            {user?.role && (
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {ROLE_LABEL[user.role] ?? user.role}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <UserIcon className="h-4 w-4" />
          {t('common.profile')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="h-4 w-4" />
          {t('nav.settings')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            const refreshToken = useAuthStore.getState().refreshToken;
            if (refreshToken) {
              // Best-effort: ignore failures so logout always proceeds locally.
              authApi.logout(refreshToken).catch(() => {});
            }
            clearSession();
            navigate('/auth/login', { replace: true });
          }}
        >
          <LogOut className="h-4 w-4" />
          {t('common.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
